import { useCallback } from 'react'

import { Icon, Chip, Table } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/react'
import { STRIPE_ENABLED } from '@services/permissions/sets/account/stripeEnabled/stripeEnabled'
import {
  cardDetails,
  editCardPaymentMethod,
} from '@services/sections/lib/billing'
import { routes } from '@services/sections/modal/routes'
import { useSegment, SegmentEvents } from '@services/segment'
import { LegalEntity, PaymentMethodType, useEntityQuery } from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import * as Buttons from '../../../buttons'

import type { CardPaymentMethod } from '@shared/graphql'

interface LinkedCardsTableRowProps {
  /**
   * The card payment method.
   */
  card: CardPaymentMethod
}

export const LinkedCardsTableRow: React.FC<LinkedCardsTableRowProps> = ({
  card,
}) => {
  const { t } = useTranslation('settings')
  const { router } = useModal()
  const route = useRouter()
  const { track } = useSegment()
  const [, { isAuthorized: isStripeEnabled }] = usePermission(STRIPE_ENABLED)
  const { data: entityData } = useEntityQuery()

  const setCardDefault = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation()
      router.push({
        pathname: routes.account.billing.paymentMethod.setDefault,
        query: {
          type: PaymentMethodType.card,
          id: card.id,
          last4: card.last4,
        },
      })
    },
    [router, card],
  )

  const navigateToCardDetails = useCallback(() => {
    if (isStripeEnabled) {
      track(SegmentEvents.EditCardPaymentMethodClick)
      void route.push({
        pathname: editCardPaymentMethod.route,
        query: {
          id: card.id,
        },
      })
    } else {
      track(SegmentEvents.CardDetailsClick)
      void route.push({
        pathname: cardDetails.route,
        query: {
          id: card.id,
        },
      })
    }
  }, [isStripeEnabled, track, route, card])

  const SetDefaultAction = useCallback(() => {
    if (card.isDefault) {
      return (
        <Chip className="mr-7 text-sm" variant="default/info">
          default
        </Chip>
      )
    }

    if (entityData?.entity?.legalEntity === LegalEntity.CIRCLE_INC) {
      return (
        <Buttons.DashboardManagement
          onClick={setCardDefault}
          size="sm"
          variant="text"
        >
          {t('billing.linkedCards.setDefault')}
        </Buttons.DashboardManagement>
      )
    }
  }, [card.isDefault, entityData?.entity?.legalEntity, setCardDefault, t])

  return (
    <Table.Body.Row
      className="cursor-pointer"
      data-testid="linked-cards-table-row"
      onClick={navigateToCardDetails}
    >
      <Table.Body.Cell className="w-12 text-neutral-strong type-body-sm">
        <span className="items-center justify-start">
          <Icon name="LinkOutline" size={18} />
        </span>
      </Table.Body.Cell>
      <Table.Body.Cell className="mt-1 w-52 text-neutral-strong type-body-sm">
        <div className="items-start justify-center">
          <div className="text-neutral-strong type-body-sm-bold">
            {`${card.network} ${t('billing.linkedCards.table.endingIn')} ${
              card.last4
            }`}
          </div>
          <div className="mt-1 whitespace-nowrap text-neutral-subtlest type-body-sm">
            {`${t('billing.linkedCards.table.expiry')} ${card.expMonth}/${
              card.expYear
            }`}
          </div>
        </div>
      </Table.Body.Cell>
      <Table.Body.Cell className="w-52 items-end justify-end text-neutral-strong type-body-sm">
        <div className="flex items-center justify-end">
          <SetDefaultAction />
          <Icon
            className="ml-4 text-icon-neutral"
            name="ChevronRightSolid"
            size={20}
          />
        </div>
      </Table.Body.Cell>
    </Table.Body.Row>
  )
}
